<template>
  <v-app class="mobile-app">
    <!-- Development Environment Alert -->
    <div v-if="env === 'dev' && showDevAlert" class="dev-alert">
      <div class="dev-alert-content">
        <v-icon size="16" color="#d97706" class="mr-2">mdi-alert</v-icon>
        <span class="dev-alert-text">Sandbox Mode: No real checks will be performed.</span>
        <button @click="showDevAlert = false" class="close-btn" aria-label="Close">x</button>
      </div>
    </div>
    <router-view />
  </v-app>
</template>

<script>
import { VIcon, VApp } from 'vuetify/lib/components'
import WidgetConfig from './components/utils/widget.config'
import { mapGetters, mapMutations } from 'vuex'
import { parseJwt } from './components/utils/utils'
import { shouldPreserveDirectMobileFlowVaultData } from './components/utils/storage'
export default {
  name: 'App',
  components: {
    VIcon,
    VApp,
  },
  data() {
    return {
      showDevAlert: true,
    }
  },
  computed: {
    ...mapGetters(['getEnv', 'getWidgetConfigFromDb']),
    env() {
      return this.getEnv || 'prod'
    },
  },
  methods: {
    ...mapMutations(['setSteps', 'setTrustedSchemaIdsAndIssuers', 'setIdDocumentLicenseKey', 'clearAllLocalStore', 'clearVaultData', 'resetMobileHandoffState', 'setEnv']),
    handleBeforeUnload() {
      this.clearAllLocalStore()
    },
    handleStorageChange(event) {
      // Listen for changes to the environment key specifically
      if (event.key === 'signin.environment' && event.newValue !== this.getEnv) {
        this.setEnv(event.newValue || 'prod')
      }
    },
  },

  mounted() {
    // Clear decrypted vault data that may have been left from a previous session.
    // VAULT_DATA_RAW contains the plaintext mnemonic/keys and must never persist across reloads.
    if (!shouldPreserveDirectMobileFlowVaultData()) {
      localStorage.removeItem('vault.vaultDataRaw')
    }
    window.addEventListener('beforeunload', this.handleBeforeUnload)
    // Initialize environment from localStorage
    const storedEnv = localStorage.getItem('signin.environment')
    if (storedEnv && !this.getEnv) {
      this.$store.commit('setEnv', storedEnv)
    }
    // Listen for localStorage changes to keep state in sync
    window.addEventListener('storage', this.handleStorageChange)
  },
  beforeDestroy() {
    window.removeEventListener('storage', this.handleStorageChange)
  },

  async created() {
    // A QR URL starts a completely fresh mobile handoff. Clear persisted
    // state before any authentication or step-selection logic reads it.
    if (new URL(window.location.href).searchParams.has('qr')) {
      this.resetMobileHandoffState()
    }

    if (WidgetConfig) {
      const steps = [
        {
          id: 0,
          isActive: true,
          stepName: 'SignIn',
          previous: 0,
          isEnabled: true,
        },
        {
          id: 1,
          isActive: false,
          stepName: 'VaultPIN',
          previous: 0,
          isEnabled: true,
        },
        {
          id: 2,
          isActive: false,
          stepName: 'AppInstructions',
          previous: 1,
          isEnabled: true,
        },
        {
          id: 3,
          isActive: false,
          stepName: 'LiveLiness3',
          name: 'Liveness Check',
          previous: 2,
          isEnabled: false,
          stepTitle: 'Conduct liveness check to prove you are a human',
          logo: 'face-id.png',
          requiredCredentialTypes: ['PersonhoodCredential'],
          hasDone: false,
        },
        {
          id: 4,
          isActive: false,
          stepName: 'IdDocs4',
          name: 'ID Verification',
          previous: 3,
          isEnabled: false,
          stepTitle: 'Submit your ID document and recieve your ID credential',
          logo: 'profile.png',
          requiredCredentialTypes: ['PassportCredential', 'GovernmentIdCredential'],
          hasDone: false,
        },
        {
          id: 5,
          isActive: false,
          stepName: 'ZkProofs',
          name: 'Proofs Generation',
          previous: 4,
          isEnabled: false,
          stepTitle: 'Prove your age',
          logo: 'proof.png',
          requiredCredentialTypes: ['zkProofOfKYC', 'zkProofOfAge', 'zkProofOfPersonHood'],
          hasDone: false,
        },
        {
          id: 6,
          isActive: false,
          stepName: 'UserConsent',
          name: 'User Consent',
          previous: 5,
          isEnabled: true,
          stepTitle: 'Provide consent of your data to the verifier app',
          logo: 'consent.png',
        },
        {
          id: 7,
          isActive: false,
          stepName: 'FinalResult',
          previous: 6,
          isEnabled: true,
        },
        {
          id: 8,
          isActive: false,
          stepName: 'SessionExpired',
          previous: 7,
          isEnabled: true,
        },
        {
          id: 9,
          isActive: false,
          stepName: 'MobileFlowUnavailable',
          previous: 0,
          isEnabled: true,
        },
      ]
      this.setSteps(steps)

      // A QR launch must always be handled by SignIn so it can fetch the new
      // mobile flow. A token cached from an earlier attempt must not bypass it.
      const isMobileHandoff = Boolean(this.$route.query.qr)
      const isDirectKycUserTokenFlow = this.getWidgetConfigFromDb?.isVaultEnabled === false && this.getWidgetConfigFromDb?.isWidgetLogin === false

      if (isDirectKycUserTokenFlow) {
        this.clearVaultData()
      }

      // Outside a QR handoff, a valid auth token can skip SignIn normally.
      const existingToken = this.$store.getters.getAuthServerAuthToken
      if (existingToken && !isMobileHandoff) {
        try {
          const payload = parseJwt(existingToken)
          const isExpired = !payload?.exp || payload.exp * 1000 < Date.now()
          if (!isExpired) {
            this.$store.commit('SET_STEP', 'VaultPIN')
          } else {
            // Token expired — clear it so the user goes through SignIn again
            this.$store.commit('setAuthServerAuthToken', '')
          }
        } catch {
          // Malformed token — clear it
          this.$store.commit('setAuthServerAuthToken', '')
        }
      }

      // trusted issuers and schemaIds
      if (WidgetConfig.schemaIds) {
        this.setTrustedSchemaIdsAndIssuers(WidgetConfig.schemaIds)
      } else {
        throw new Error('Invalid widget configuration, schemaIds is required')
      }

      // set license key
      if (WidgetConfig.licenseKey) {
        if (WidgetConfig.env === 'dev') {
          this.setIdDocumentLicenseKey(WidgetConfig.licenseKey.dev)
        } else if (WidgetConfig.env === 'prod') {
          this.setIdDocumentLicenseKey(WidgetConfig.licenseKey.prod)
        } else {
          this.setIdDocumentLicenseKey(WidgetConfig.licenseKey.dev)
        }
      }
    } else {
      throw new Error('Invalid widget configuration')
    }
  },
}
</script>

<style>
.mobile-app {
  width: 100%;
  height: 100%;
  background-color: #ffffff;
  overflow: hidden;
}

#app {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: #333333;
  width: 100%;
  height: 100%;
  background-color: #ffffff;
  overflow: hidden;
}

.dev-alert {
  position: sticky;
  top: 0;
  z-index: 1000;
  background-color: #fffbeb;
  border-bottom: 1px solid #fed7aa;
  padding: 8px 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.dev-alert-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  max-width: 375px;
  margin: 0 auto;
}

.dev-alert-text {
  flex: 1;
  color: #666666;
  font-size: 12px;
  font-weight: 500;
  line-height: 1.4;
  margin: 0 8px;
}

.dev-alert .close-btn {
  min-width: 24px !important;
  width: 24px !important;
  height: 24px !important;
  padding: 0 !important;
  margin: 0 !important;
  border-radius: 4px !important;
  background-color: rgba(0, 0, 0, 0.08) !important;
  border: 1px solid #c88d4b7c !important;
  cursor: pointer;
  font-size: 18px;
  line-height: 1;
  color: #c39d71 !important;
  font-weight: bold;
  display: flex;
  align-items: center;
  justify-content: center;
}

.dev-alert .close-btn:hover {
  background-color: rgba(0, 0, 0, 0.12) !important;
  border-color: #504436 !important;
}
</style>
